<template>
  <div class="chat-page">
    <!-- 移动端遮罩层 -->
    <div
      v-if="sidebarVisible && isMobile"
      class="sidebar-overlay"
      @click="toggleSidebar"
    ></div>

    <div class="chat-container">
      <!-- 对话列表 -->
      <ConversationList
        :class="{
          'sidebar-visible': sidebarVisible,
          'mobile-sidebar': isMobile,
        }"
        @conversation-selected="onConversationSelected"
        @load-messages="onLoadMessages"
        @status-changed="onStatusChanged"
      />

      <!-- 聊天区域 -->
      <!-- :style="chatAreaStyle" -->
      <ChatArea
        ref="chatArea"
        @toggle-sidebar="toggleSidebar"
        :show-sidebar-toggle="isMobile"
        :staff-status="staffStatus"
      />
    </div>
  </div>
</template>

<script>
import ConversationList from "@/components/ConversationList.vue";
import ChatArea from "@/components/ChatArea.vue";
import wsService from "@/services/websocket.js";
import { formatBeijingTime } from '@/utils/time'

export default {
  name: "Chat",
  components: {
    ConversationList,
    ChatArea,
  },
  data() {
    return {
      sidebarVisible: true,
      wsConnected: false,
      isMobile: false,
      refreshInterval: null,
      isRefreshing: false,
      readTimer: null,
      autoRefreshInterval: 10000, // 10秒自动刷新一次
      hasInitialized: false, // 标记是否已经完成初始化
      staffStatus: "online", // 客服状态：online|busy
    };
  },
  computed: {
    activeConversation() {
      return this.$store.getters.activeConversation;
    },
    chatAreaStyle() {
      if (this.isMobile) {
        return {};
      }
      return { marginLeft: this.sidebarVisible ? "320px" : "0" };
    },
  },
  mounted() {
    // 检测是否为移动设备
    this.checkMobile();
    window.addEventListener("resize", this.checkMobile);
    if(window.wsService){
      window.wsService.disconnect();
    }
    // 设置全局WebSocket服务
    window.wsService = wsService;

    // 从API加载最新数据
    this.$store.dispatch("loadConversations").catch((error) => {
      console.error("Failed to load conversations:", error);
      this.$message.error("加载对话列表失败");
    });

    // 启动自动刷新
    this.startAutoRefresh();

    // 监听 ConversationList 发出的消息定位事件，转发给 ChatArea
    this.$on('locate-message', (data) => {
      if (this.$refs.chatArea) {
        this.$refs.chatArea.handleLocateMessage(data);
      }
    });
  },
  methods: {
    checkMobile() {
      this.isMobile = window.innerWidth <= 768;
      if (this.isMobile) {
        this.sidebarVisible = false;
      } else {
        this.sidebarVisible = true;
      }
    },
    toggleSidebar() {
      this.sidebarVisible = !this.sidebarVisible;
    },
    onConversationSelected(conversation) {
      // 在移动端选择对话后自动隐藏侧边栏
      if (this.isMobile) {
        this.sidebarVisible = false;
      }

      // 连接WebSocket（如果需要）
      if (conversation && !this.wsConnected) {
        this.connectWebSocket(conversation);
      }
    },
    onStatusChanged(status) {
      console.log("客服状态已变更:", status);
      // 更新本地状态
      this.staffStatus = status;
      
      if (status === 'busy') {
        // 忙碌状态：停止刷新并清理WebSocket处理器
        this.stopAutoRefresh();
        // 清理WebSocket消息处理器，防止任何遗留的消息处理
        if (wsService) {
          console.log('清理WebSocket消息处理器');
          wsService.removeMessageHandler(this.handleWebSocketMessage);
          wsService.removeSystemHandler(this.handleWebSocketSystemEvent);
          // 重置连接状态标志
          this.wsConnected = false;
          // 断开连接
          wsService.disconnect();
        }
      } else if (status === 'online') {
        // 上线状态：启动自动刷新，并确保消息处理器被添加
        this.startAutoRefresh();
        // 重置连接状态，强制重新连接并添加处理器
        this.wsConnected = false;
        console.log('上线状态：开始连接WebSocket并添加处理器');
        this.connectWebSocket();
      }
    },
    onLoadMessages() {
      // 调用store加载指定对话的历史消息
      // this.$store.dispatch("loadMessages", {
      //   conversationId: conversation.customId,
      //   conversation: conversation,
      // });
    },
    async connectWebSocket(conversation) {
      // 如果已经连接且处理器已添加，则跳过
      if (this.wsConnected && wsService.messageHandlers && wsService.messageHandlers.length > 0) {
        console.log("WebSocket already connected with handlers");
        return;
      }
      
      try {
        // 使用 login_id 作为连接账号，不依赖 conversation
        const loginId = (conversation && conversation.senderAccount) || localStorage.getItem('login_id');
        if (!loginId) {
          console.error("无法获取登录账号");
          this.$message.error("登录信息丢失，请重新登录");
          return;
        }
        
        // 如果还没连接，则进行连接
        if (!this.wsConnected) {
          await wsService.connect(loginId);
          this.wsConnected = true;
          console.log("WebSocket connected successfully");
        }

        // 始终确保处理器被添加
        wsService.addMessageHandler(this.handleWebSocketMessage);
        wsService.addSystemHandler(this.handleWebSocketSystemEvent);
        console.log("WebSocket handlers added successfully");
      } catch (error) {
        console.error("Failed to connect WebSocket:", error);
        this.wsConnected = false;
        // 仅在控制台输出错误日志，不显示用户提示
      }
    },
    handleWebSocketMessage(message) {
      console.log("Received WebSocket message:12", message);
      if (message.event === "READ" && message.data) {
        this.$store.commit("MARK_MESSAGES_READ", message.data);
        return;
      }
      
      // 先处理消息，再播放声音，确保消息先显示
      let shouldPlaySound = false;
      const loginId = localStorage.getItem("login_id");

      // 处理其他客服发送的消息（只在当前对话中处理）
      if (this.activeConversation && (message.data.senderId !== this.activeConversation.customerId)) {
        if(message.data.sessionId === this.activeConversation.id){
          console.log("Same sessionId,说明这个是别客服发的 也要发到自己那.");
          this.$refs.chatArea.sendMessageByOther(message.data);
        }
        // 注意：这里不return，因为即使不是当前对话的消息，我们也需要播放声音
      }
      
      // 处理WebSocket MESSAGE事件
      if (message.event === "MESSAGE" && message.data) {
        const msgData = message.data;
        
        // 检查是否需要播放声音（只有来自客户的消息才播放）
        if (msgData.senderId != loginId) {
          shouldPlaySound = true;
          console.log("需要播放声音：消息来自客户", msgData.senderId, "当前登录客服", loginId, "活动对话:", this.activeConversation ? this.activeConversation.customerId : "无");
          if (msgData.sessionId) {
            this.$store.commit("MARK_MESSAGES_READ", {
              sessionId: msgData.sessionId,
              readerId: msgData.senderId
            });
          }
        }

        // 获取对话ID（即使没有活动对话，我们也需要知道消息属于哪个对话）
        let conversationId = null;
        let isCurrentConversation = false;
        
        if (this.activeConversation) {
          // 检查消息是否属于当前活动对话
          if (msgData.sessionId === this.activeConversation.id) {
            conversationId = this.activeConversation.customId;
            isCurrentConversation = true;
            console.log("消息属于当前活动对话");
          } else {
            // 消息不属于当前活动对话，但仍然需要处理
            console.log("消息不属于当前活动对话，但需要处理");
          }
        }
        
        // 如果 conversationId 还没有设置，尝试构建
        if (!conversationId && msgData.sessionId && msgData.receiverId) {
          // 构建对话ID格式：conv_{staffId}_{sessionId}
          conversationId = `conv_${msgData.receiverId}_${msgData.sessionId}`;
          console.log("构建对话ID:", conversationId);
        }

        // 如果有对话ID，将消息添加到store
        if (conversationId) {
          const newMessage = {
            id: msgData.messageId,
            content: msgData.content,
            type: msgData.messageType,
            sender: msgData.senderId,
            receiver: msgData.receiverId,
            createTime: formatBeijingTime(), // 使用北京时间
            isOwn: false,
          };
          console.log("Handling WebSocket message for conversation:", conversationId, "isCurrent:", isCurrentConversation);

          console.log("Adding WebSocket message to store:", newMessage);

          this.$store.commit("ADD_MESSAGE", {
            conversationId: conversationId,
            message: newMessage,
          });
          
          // 检查 ChatArea 组件引用是否可用
          const chatAreaRef = this.$refs.chatArea;
          console.log("ChatArea 引用检查:", {
            hasRef: !!chatAreaRef,
            refType: chatAreaRef ? typeof chatAreaRef : "undefined",
            hasPlayNotificationSound: chatAreaRef && typeof chatAreaRef.playNotificationSound === 'function'
          });
          
          // 播放声音的辅助函数
          const playSoundIfNeeded = () => {
            if (!shouldPlaySound) return;
            
            // 首先尝试使用 ChatArea 组件的方法
            if (chatAreaRef && typeof chatAreaRef.playNotificationSound === 'function') {
              console.log("使用 ChatArea 组件播放声音");
              chatAreaRef.playNotificationSound();
              return;
            }
            
            // 如果 ChatArea 引用不可用，尝试直接播放声音
            console.log("ChatArea 引用不可用，尝试直接播放声音");
            this.playNotificationSoundDirectly();
          };
          
          // 如果是当前对话，滚动到底部
          if (isCurrentConversation) {
            this.markCurrentSessionRead();
            this.$nextTick(() => {
              if (chatAreaRef && typeof chatAreaRef.scrollToBottom === 'function') {
                chatAreaRef.scrollToBottom();
              }
              
              // 在滚动完成后播放声音
              playSoundIfNeeded();
            });
          } else {
            // 不是当前对话，立即播放声音（用户在其他页面或查看其他对话）
            playSoundIfNeeded();
          }
        } else {
          // 无法确定对话ID，但仍然尝试播放声音
          console.log("无法确定对话ID，但收到了新消息");
          
          if (shouldPlaySound) {
            this.playNotificationSoundDirectly();
          }
        }
      }
    },
    markCurrentSessionRead() {
      clearTimeout(this.readTimer);
      this.readTimer = setTimeout(() => {
        if (this.activeConversation) {
          this.$http.post('/api/session/read', { sessionId: this.activeConversation.id })
            .catch(error => console.warn('Mark session read failed:', error));
        }
      }, 400);
    },

    // 直接播放通知声音（当 ChatArea 引用不可用时使用）
    playNotificationSoundDirectly() {
      try {
        // 检查全局声音设置
        const globalSoundEnabled = localStorage.getItem('globalSoundEnabled') !== 'false';
        console.log('直接播放声音 - 检查声音设置:', globalSoundEnabled);
        if (!globalSoundEnabled) {
          console.log('声音已禁用');
          return;
        }

        // 使用 Web Audio API 生成"滴滴滴"声音
        let audioContext = window.audioContext;
        
        // 如果 audioContext 还没有创建，则创建
        if (!audioContext) {
          audioContext = new (window.AudioContext || window.webkitAudioContext)();
          window.audioContext = audioContext;
          console.log('直接播放声音 - 创建新的 AudioContext');
        }
        
        // 立即播放声音，不等待 AudioContext 恢复
        if (audioContext.state === 'suspended') {
          console.log('直接播放声音 - AudioContext 处于 suspended 状态，立即尝试恢复并播放');
          audioContext.resume().then(() => {
            console.log('直接播放声音 - AudioContext 恢复成功，立即播放声音');
            this._playBeepDirectly(audioContext);
          }).catch(err => {
            console.error('直接播放声音 - 恢复 AudioContext 失败:', err);
            // 即使恢复失败也尝试播放
            try {
              this._playBeepDirectly(audioContext);
            } catch (playError) {
              console.error('直接播放声音 - 播放声音失败:', playError);
            }
          });
        } else {
          // AudioContext 已经是 running 状态，立即播放
          this._playBeepDirectly(audioContext);
        }
      } catch (error) {
        console.error('直接播放声音 - 播放通知声音失败:', error);
      }
    },

    // 实际播放声音的方法（直接版本）
    _playBeepDirectly(audioContext) {
      const now = audioContext.currentTime;
      
      // 创建振荡器和增益节点
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      // 设置频率 (800 Hz)
      oscillator.frequency.value = 800;
      oscillator.type = 'sine';
      
      // 播放3次"滴"声，每次100ms，间隔200ms
      const beepDuration = 0.1; // 100ms
      const beepInterval = 0.2; // 200ms
      
      gainNode.gain.setValueAtTime(0, now);
      
      for (let i = 0; i < 3; i++) {
        const startTime = now + i * beepInterval;
        const endTime = startTime + beepDuration;
        
        gainNode.gain.setValueAtTime(0.3, startTime);
        gainNode.gain.setValueAtTime(0, endTime);
      }
      
      oscillator.start(now);
      // 计算总的持续时间
      const totalDuration = 2 * beepInterval + beepDuration;
      oscillator.stop(now + totalDuration);
      
      console.log('直接播放声音 - 声音播放已启动');
    },
    handleWebSocketSystemEvent(event, data) {
      console.log("Received WebSocket system event:00", event, data);

      // 处理撤回消息事件
      if (event === "recall" && data && data.messageId) {
        // 调用store的撤回消息mutation
        this.$store.commit("RECALL_MESSAGE", {
          conversationId: this.activeConversation.customId,
          messageId: data.messageId,
        });

        console.log("Message recalled:", data.messageId);
      }
    },
    // 开始自动刷新
    startAutoRefresh() {
      // 清除已有的定时器
      this.stopAutoRefresh();

      // 设置新的定时器
      this.refreshInterval = setInterval(() => {
        this.refreshConversations();
      }, this.autoRefreshInterval);
      console.log(`自动刷新已启动，间隔: ${this.autoRefreshInterval}ms`);
    },
    // 停止自动刷新
    stopAutoRefresh() {
      if (this.refreshInterval) {
        clearInterval(this.refreshInterval);
        this.refreshInterval = null;
        console.log("自动刷新已停止");
      }
    },
    // 刷新对话列表
    async refreshConversations() {
      // 如果正在刷新，则跳过
      if (this.isRefreshing) return;

      this.isRefreshing = true;
      try {
        // console.log('开始刷新对话列表...');

        // 记住当前活动对话ID
        // const currentActiveId = this.$store.state.activeConversationId;

        // 刷新对话列表
        await this.$store.dispatch("loadConversations");

        // console.log('对话列表刷新完成');

        // const conversations = this.$store.state.conversations;
        // // 只有在页面刚加载且没有任何对话被选中时，才自动选择第一个对话
        // // 避免在自动刷新过程中意外选中对话
        // if (!currentActiveId && conversations.length > 0 && !this.hasInitialized) {
        //   this.hasInitialized = true;
        //   this.$store.commit('SET_ACTIVE_CONVERSATION', conversations[0].id);
        // }
      } catch (error) {
        console.error("刷新对话列表失败:", error);
      } finally {
        this.isRefreshing = false;
      }
    },
  },
  beforeDestroy() {
    // 停止自动刷新
    this.stopAutoRefresh();

    // 移除事件监听器
    window.removeEventListener("resize", this.checkMobile);

    // 移除WebSocket处理器
    if (wsService) {
      wsService.removeMessageHandler(this.handleWebSocketMessage);
      wsService.removeSystemHandler(this.handleWebSocketSystemEvent);
      wsService.disconnect();
    }
  },
};
</script>

<style scoped>
.chat-page {
  height: 100vh;
  overflow: hidden;
  position: relative;
}

.chat-container {
  display: flex;
  height: 100vh;
}

.sidebar-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 999;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .chat-container {
    position: relative;
  }

  .mobile-sidebar {
    transform: translateX(-100%);
    transition: transform 0.3s ease;
  }

  .mobile-sidebar.sidebar-visible {
    transform: translateX(0);
  }
}

@media (max-width: 480px) {
  .chat-page {
    font-size: 14px;
  }
}
</style>
